/**
 * PATH: packages/ui/src/components/settings/sender-profiles/PersonMergeQueue.tsx
 * FILE_ID: UI-SETTINGS-PERSONMERGE-028
 * PURPOSE: W3-V8-P4 person-merge review queue — "are these two contacts
 *          the same person?" cards with Same person / Different people.
 * WHY: Splink never auto-merges (precision-first); the queue drained
 *      only via CLI, so nobody drained it. This is its human loop.
 * LANDMINE: self-suppressing — renders NOTHING when the queue is empty
 *      or the fetch fails, so orgs without candidates see no clutter.
 * BACKEND: quotech-backend email_kanban/views/senders.py merges
 *      endpoints (cross-repo, not a resolvable RELATED edge).
 * RELATED: UI-SETTINGS-SENDERS-027 (mounts this under the Senders list).
 */

import { useCallback, useEffect, useState } from 'react'
import { GitMerge, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { SettingsApiClient } from '../types'
import type { PersonMergeCandidate, PersonSummary } from './types'
import { fetchMergeCandidates, sendMergeVerdict } from './senderProfilesApi'

const QUEUE_PAGE = 10

function PersonCard({ person }: { person: PersonSummary }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
        {person.display_name || person.primary_email || 'Unnamed contact'}
      </p>
      {person.primary_email && (
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {person.primary_email}
        </p>
      )}
      <p className="text-[11px] text-slate-400">
        {person.link_count} linked record{person.link_count === 1 ? '' : 's'}
      </p>
    </div>
  )
}

export interface PersonMergeQueueProps {
  apiClient?: SettingsApiClient
  className?: string
}

export function PersonMergeQueue({ apiClient, className }: PersonMergeQueueProps) {
  const [candidates, setCandidates] = useState<PersonMergeCandidate[]>([])
  const [total, setTotal] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!apiClient) return
    try {
      const data = await fetchMergeCandidates(apiClient, { limit: QUEUE_PAGE })
      setCandidates(data.candidates)
      setTotal(data.total)
    } catch {
      // Self-suppressing: a failed read leaves the section invisible.
      setCandidates([])
      setTotal(0)
    } finally {
      setLoaded(true)
    }
  }, [apiClient])

  useEffect(() => {
    load()
  }, [load])

  const handleVerdict = useCallback(
    async (candidateId: string, verdict: 'accept' | 'reject') => {
      if (!apiClient) return
      setBusyId(candidateId)
      setError(null)
      try {
        await sendMergeVerdict(apiClient, {
          candidate_id: candidateId,
          verdict,
        })
        await load()
      } catch {
        setError('Could not record the verdict.')
      } finally {
        setBusyId(null)
      }
    },
    [apiClient, load],
  )

  if (!apiClient || !loaded || candidates.length === 0) return null

  return (
    <div className={cn('space-y-3', className)}>
      <div className="pt-2">
        <div className="flex items-center gap-2">
          <GitMerge className="w-4 h-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
            Possible duplicate contacts
          </h4>
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            {total}
          </span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          The AI thinks each pair may be the same person under two
          addresses. Confirming merges their history; rejecting keeps
          them separate for good.
        </p>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
        {candidates.map((c) => {
          const busy = busyId === c.id
          return (
            <div key={c.id} className="px-3 py-2.5">
              <div className="flex items-center gap-3">
                <PersonCard person={c.person_a} />
                <span
                  className="text-[11px] text-slate-400 shrink-0"
                  title={`Match probability ${(c.match_probability * 100).toFixed(0)}%`}
                >
                  {(c.match_probability * 100).toFixed(0)}%
                </span>
                <PersonCard person={c.person_b} />
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <button
                  onClick={() => handleVerdict(c.id, 'accept')}
                  disabled={busy}
                  className="px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                >
                  Same person
                </button>
                <button
                  onClick={() => handleVerdict(c.id, 'reject')}
                  disabled={busy}
                  className="px-2.5 py-1 rounded-md text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40"
                >
                  Different people
                </button>
                {busy && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                )}
              </div>
            </div>
          )
        })}
      </section>
    </div>
  )
}

export default PersonMergeQueue
